import { startTransition, useEffect, useRef, useState } from 'react'
import getUniqueId from 'helpers/getUniqueId'

import { addUniqueGuids } from './helpers/addUniqueGuids'
import type { PageEditorRowType } from './PageEditor.types'
import type { PageEditorRevision } from './usePageEditorRevisions'

type UsePageEditorRowsParams = {
    entityRows: any[] | null | undefined
    activeRevision: PageEditorRevision | null
    versionParam: string | null
}

type ServerRows = Parameters<typeof addUniqueGuids>[0]

const usePageEditorRows = ({
    entityRows,
    activeRevision,
    versionParam,
}: UsePageEditorRowsParams) => {
    // Skip dirty flag when rows change from server sync or discard.
    const skipRowsDirtyRef = useRef(false)
    // Ignore the first rows effect run on mount.
    const isFirstRowsChange = useRef(true)

    // Skip re-applying rows that were already set on mount.
    const lastAppliedRevisionGuidRef = useRef<string | null>(
        versionParam && activeRevision?.content?.rows
            ? activeRevision.guid
            : null,
    )

    const [rows, setRows] = useState<PageEditorRowType[]>(() => {
        // Start from the URL revision when present, otherwise from the page entity.
        if (versionParam) {
            return activeRevision?.content?.rows
                ? addUniqueGuids(activeRevision.content.rows)
                : []
        }

        return addUniqueGuids(entityRows || [])
    })
    const [isDirty, setIsDirty] = useState(false)

    // Replace rows from the server without marking the editor dirty.
    const replaceRows = (sourceRows: ServerRows) => {
        skipRowsDirtyRef.current = true

        startTransition(() => {
            setRows(addUniqueGuids(sourceRows))
            setIsDirty(false)
        })
    }

    // Keep editor rows in sync when the URL revision changes.
    useEffect(() => {
        if (!versionParam) {
            if (lastAppliedRevisionGuidRef.current === null) {
                return
            }

            lastAppliedRevisionGuidRef.current = null
            replaceRows(entityRows || [])
            return
        }

        const revisionGuid = activeRevision?.guid
        const revisionRows = activeRevision?.content?.rows

        if (!revisionGuid || !revisionRows) {
            return
        }
        if (lastAppliedRevisionGuidRef.current === revisionGuid) {
            return
        }

        lastAppliedRevisionGuidRef.current = revisionGuid
        replaceRows(revisionRows)
    }, [versionParam, entityRows, activeRevision])

    // Mark the editor dirty when the user edits rows.
    useEffect(() => {
        if (isFirstRowsChange.current) {
            isFirstRowsChange.current = false
            skipRowsDirtyRef.current = false
            return
        }

        if (!isDirty && !skipRowsDirtyRef.current) {
            setIsDirty(true)
        }

        skipRowsDirtyRef.current = false
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to row edits, not isDirty resets from save
    }, [rows])

    // Reset editor rows to the active revision or page entity.
    const handleDiscardChanges = () => {
        replaceRows(activeRevision?.content?.rows ?? entityRows ?? [])
    }

    const handleAddRow = () => {
        setRows([
            ...rows,
            {
                isFullWidth: false,
                backgroundColor: 'white',
                columns: [{ width: [12], widgets: [] }],
                rowGuid: getUniqueId('row'),
            },
        ])
        window.setTimeout(() => {
            const btn = document.getElementById(`${rows.length}-0-add-widget`)
            btn?.focus()
        }, 0)
    }

    return {
        rows,
        setRows,
        isDirty,
        setIsDirty,
        handleAddRow,
        handleDiscardChanges,
    }
}

export default usePageEditorRows
